import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsAPI } from '../utils/api';
import { useSiteStore } from '../stores/siteStore';
import clsx from 'clsx';
import { formatDateTime } from '../utils/formatDateTime';
import toast from 'react-hot-toast';
import NotificationDetailModal from '../components/NotificationDetailModal';
import {
  BellIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

export default function NotificationsPage() {
  const [activeTab, setActiveTab] = useState('settings');

  const tabs = [
    { id: 'settings', label: 'Channels & Preferences', icon: BellIcon },
    { id: 'escalation', label: 'Escalation Rules', icon: ArrowPathIcon },
    { id: 'history', label: 'Notification History', icon: ClockIcon },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="animate-fade-in">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold gradient-text">Alert Management</h1>
            <p className="text-slate-700 mt-2 text-base font-medium">Stay informed with intelligent, customizable notifications tailored to your safety needs</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500/25 to-pink-500/15 border border-purple-500/40 rounded-xl p-4">
            <p className="text-sm text-slate-700 font-semibold">
              <span className="font-bold">💡 Tip:</span> Critical alerts always reach you, even during quiet hours
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="glass rounded-xl p-1 inline-flex animate-fade-in" style={{ animationDelay: '0.1s' }}>
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all duration-300',
              activeTab === tab.id
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                : 'text-slate-700 hover:text-slate-900 bg-slate-200 hover:bg-slate-300'
            )}
          >
            <tab.icon className="w-5 h-5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="animate-fade-in" style={{ animationDelay: '0.2s' }}>
        {activeTab === 'settings' && <NotificationSettings />}
        {activeTab === 'escalation' && <EscalationRules />}
        {activeTab === 'history' && <NotificationHistory />}
      </div>
    </div>
  );
}

function NotificationSettings() {
  const queryClient = useQueryClient();
  const [lastUpdated, setLastUpdated] = useState(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['notification-settings'],
    queryFn: notificationsAPI.getSettings,
  });

  const updateMutation = useMutation({
    mutationFn: notificationsAPI.updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-settings'] });
      setLastUpdated(new Date());
      toast.success('✓ Settings updated and applied immediately', {
        duration: 3000,
        icon: '🔔',
      });
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update settings');
    },
  });

  const testMutation = useMutation({
    mutationFn: notificationsAPI.testNotification,
    onSuccess: () => {
      toast.success('✓ Test notification sent! Check your configured channels.', {
        duration: 4000,
        icon: '📬',
      });
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Test notification failed - check if channel is enabled');
    },
  });

  const handleToggle = (field) => {
    updateMutation.mutate({
      [field]: !settings?.data?.[field],
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500/30 border-t-purple-500"></div>
      </div>
    );
  }

  const currentSettings = settings?.data || {};

  return (
    <div className="space-y-6">
      {/* Status Bar */}
      {lastUpdated && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 animate-fade-in">
          <p className="text-sm text-emerald-300">
            ✓ All changes are active and will apply to future alerts
          </p>
        </div>
      )}

      {/* Notification Channels */}
      <div className="glass-card p-6">
        <div className="mb-6">
          <h3 className="text-3xl font-bold text-white mb-3">📢 Notification Channels</h3>
          <p className="text-base text-slate-700 font-semibold">Choose how you'd like to receive safety alerts. Each channel can be customized based on alert severity.</p>
        </div>

        <div className="space-y-4">
          <ChannelToggle
            icon={EnvelopeIcon}
            title="Email Notifications"
            subtitle="Professional inbox delivery"
            description="Get detailed alerts in your inbox with full incident information, snapshots, and action links"
            enabled={currentSettings.emailEnabled}
            onToggle={() => handleToggle('emailEnabled')}
            onTest={() => testMutation.mutate('email')}
            isLoading={updateMutation.isPending}
            status={currentSettings.emailEnabled ? 'Active - Receiving emails' : 'Inactive'}
          />

          <ChannelToggle
            icon={DevicePhoneMobileIcon}
            title="SMS Notifications"
            subtitle="Instant text alerts"
            description="Receive critical and high-severity alerts as text messages for immediate awareness"
            enabled={currentSettings.smsEnabled}
            onToggle={() => handleToggle('smsEnabled')}
            onTest={() => testMutation.mutate('sms')}
            isLoading={updateMutation.isPending}
            status={currentSettings.smsEnabled ? 'Active - SMS enabled' : 'Inactive'}
          />

          <ChannelToggle
            icon={ComputerDesktopIcon}
            title="Push Notifications"
            subtitle="Real-time mobile & browser alerts"
            description="Get instant push notifications on your mobile device and browser for immediate incident awareness"
            enabled={currentSettings.pushEnabled}
            onToggle={() => handleToggle('pushEnabled')}
            onTest={() => testMutation.mutate('push')}
            isLoading={updateMutation.isPending}
            status={currentSettings.pushEnabled ? 'Active - Receiving pushes' : 'Inactive'}
          />
        </div>
      </div>

      {/* Severity Filters */}
      <div className="glass-card p-6">
        <div className="mb-6">
          <h3 className="text-3xl font-bold text-white mb-3">🎯 Smart Filtering</h3>
          <p className="text-base text-slate-700 font-semibold">
            Control which alert severity levels trigger notifications. Higher severity alerts are always delivered.
          </p>
        </div>

        <div className="space-y-4">
          <div className="bg-slate-100 rounded-lg p-3 border border-slate-200">
            <p className="text-xs text-slate-700 font-semibold">
              <span className="font-bold">✓</span> Select severity levels to receive notifications for:
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map((severity) => {
              const isSelected = currentSettings.severityFilter?.includes(severity);
              const severityDescriptions = {
                CRITICAL: 'Life-threatening violations',
                HIGH: 'Major safety violations',
                MEDIUM: 'Moderate concerns',
                LOW: 'Minor observations',
              };
              const colorConfig = {
                CRITICAL: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', emoji: '🚨' },
                HIGH: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30', emoji: '⚠️' },
                MEDIUM: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30', emoji: '⚡' },
                LOW: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30', emoji: 'ℹ️' },
              };
              const config = colorConfig[severity];

              return (
                <div key={severity} className="relative group">
                  <button
                    onClick={() => {
                      const current = currentSettings.severityFilter || [];
                      const updated = isSelected
                        ? current.filter((s) => s !== severity)
                        : [...current, severity];
                      updateMutation.mutate({ severityFilter: updated });
                    }}
                    className={clsx(
                      'px-5 py-3 rounded-xl border-2 font-bold transition-all duration-300 flex items-center gap-2 text-base',
                      isSelected
                        ? `${config.bg} ${config.text} ${config.border} shadow-xl`
                        : 'bg-slate-200 text-slate-700 border-slate-300 hover:border-slate-400 hover:text-slate-900'
                    )}
                    title={severityDescriptions[severity]}
                  >
                    <span>{config.emoji}</span>
                    {isSelected && <CheckIcon className="w-5 h-5" />}
                    {severity}
                  </button>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-slate-800 text-white text-xs py-1 px-2 rounded whitespace-nowrap pointer-events-none z-50">
                    {severityDescriptions[severity]}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Quiet Hours */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-3xl font-bold text-white mb-2">🌙 Do Not Disturb Schedule</h3>
            <p className="text-base text-slate-700 font-semibold">
              Reduce non-critical notifications during your downtime. Critical alerts always come through.
            </p>
          </div>
          <button
            onClick={() => handleToggle('quietHoursEnabled')}
            className={clsx(
              'relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-300',
              currentSettings.quietHoursEnabled
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 shadow-lg shadow-purple-500/50'
                : 'bg-white/20'
            )}
          >
            <span
              className={clsx(
                'inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform duration-300',
                currentSettings.quietHoursEnabled ? 'translate-x-6' : 'translate-x-1'
              )}
            />
          </button>
        </div>

        {currentSettings.quietHoursEnabled && (
          <div className="space-y-4 animate-fade-in">
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg p-3">
              <p className="text-xs text-gray-300">
                <span className="font-semibold">⏰ Active:</span> Non-critical alerts will be silenced from {currentSettings.quietHoursStart || '22:00'} to {currentSettings.quietHoursEnd || '07:00'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Start Time</label>
                <div className="relative">
                  <input
                    type="time"
                    value={currentSettings.quietHoursStart || '22:00'}
                    onChange={(e) => updateMutation.mutate({ quietHoursStart: e.target.value })}
                    className="input"
                  />
                  <span className="text-xs text-slate-700 mt-1 block font-bold">When quiet hours begin</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">End Time</label>
                <div className="relative">
                  <input
                    type="time"
                    value={currentSettings.quietHoursEnd || '07:00'}
                    onChange={(e) => updateMutation.mutate({ quietHoursEnd: e.target.value })}
                    className="input"
                  />
                  <span className="text-xs text-slate-700 mt-1 block font-bold">When quiet hours end</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {!currentSettings.quietHoursEnabled && (
          <div className="bg-slate-100 rounded-lg p-3 border border-slate-200 text-center">
            <p className="text-sm text-slate-700 font-semibold">
              <span className="font-bold">Currently Off:</span> All alerts will be delivered regardless of time
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function ChannelToggle({ icon: Icon, title, subtitle, description, enabled, onToggle, onTest, isLoading, status }) {
  return (
    <div className={clsx(
      'p-6 glass rounded-xl transition-all duration-300 border-2',
      enabled ? 'border-purple-500/50 bg-gradient-to-r from-white/12 to-purple-500/15 shadow-lg shadow-purple-500/15' : 'border-white/15 hover:border-white/25 bg-white/5'
    )}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4 flex-1">
          <div className={clsx(
            'w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 transition-all',
            enabled ? 'bg-gradient-to-br from-purple-500/50 to-pink-500/40 shadow-lg shadow-purple-500/30' : 'bg-slate-200'
          )}>
            <Icon className={clsx('w-8 h-8', enabled ? 'text-purple-600' : 'text-slate-600')} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <p className="font-bold text-white text-xl leading-tight">{title}</p>
              {enabled && (
                <span className="text-xs font-bold px-3 py-1.5 bg-emerald-500/30 text-emerald-300 rounded-full border border-emerald-500/50 uppercase tracking-wide">
                  Active
                </span>
              )}
            </div>
            <p className="text-base text-slate-700 font-bold mb-2 mt-1">{subtitle}</p>
            <p className="text-sm text-slate-600 leading-relaxed font-medium">{description}</p>
            {status && (
              <p className={clsx(
                'text-sm mt-3 font-bold',
                enabled ? 'text-emerald-700' : 'text-slate-600'
              )}>
                → {status}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 ml-4">
          <button
            onClick={onTest}
            disabled={!enabled || isLoading}
            className={clsx(
              'px-4 py-2.5 text-base font-bold rounded-lg transition-all',
              isLoading || !enabled
                ? 'bg-slate-400 text-slate-600 cursor-not-allowed opacity-60'
                : 'bg-purple-600 text-white hover:bg-purple-700 border-2 border-purple-700 shadow-md hover:shadow-lg'
            )}
            title={enabled ? 'Send a test notification via this channel' : 'Enable this channel to test'}
          >
            {isLoading ? '⏳' : '📬 Test'}
          </button>
          <button
            onClick={onToggle}
            disabled={isLoading}
            className={clsx(
              'relative inline-flex h-8 w-14 items-center rounded-full transition-all duration-300 flex-shrink-0',
              enabled ? 'bg-gradient-to-r from-emerald-500 to-green-500 shadow-lg shadow-emerald-500/30' : 'bg-slate-400 hover:bg-slate-500 shadow-md'
            )}
          >
            <span
              className={clsx(
                'inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform duration-300',
                enabled ? 'translate-x-7' : 'translate-x-1'
              )}
            />
          </button>
        </div>
      </div>
    </div>
  );
}

function EscalationRules() {
  const queryClient = useQueryClient();
  const { selectedSite } = useSiteStore();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const siteId = selectedSite?.id;

  const { data, isLoading } = useQuery({
    queryKey: ['escalation-rules', siteId],
    queryFn: () => notificationsAPI.getEscalationRules(siteId),
    enabled: !!siteId,
  });

  const deleteMutation = useMutation({
    mutationFn: notificationsAPI.deleteEscalationRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['escalation-rules', siteId] });
      toast.success('Rule deleted');
    },
    onError: () => toast.error('Failed to delete rule'),
  });

  const rules = data?.data || [];

  if (!siteId) {
    return (
      <div className="glass-card p-12 text-center">
        <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
          <ArrowPathIcon className="w-10 h-10 text-purple-400" />
        </div>
        <h3 className="text-2xl font-black text-slate-800 mb-2">Select a Site</h3>
        <p className="text-slate-700 font-semibold">Please select a site from the header to manage escalation rules</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500/30 border-t-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-3xl font-bold text-white mb-2">⚡ Auto-Escalation Rules</h3>
          <p className="text-base text-slate-700 font-semibold">
            Automatically escalate unacknowledged critical alerts to supervisors and managers after a specified time. Ensure no alert goes unnoticed.
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary flex items-center gap-2 whitespace-nowrap"
        >
          <PlusIcon className="w-5 h-5" />
          Create Rule
        </button>
      </div>

      {rules.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
            <ArrowPathIcon className="w-12 h-12 text-purple-400" />
          </div>
          <h3 className="text-2xl font-black text-slate-800 mb-3">No Escalation Rules Yet</h3>
          <p className="text-slate-700 font-semibold mb-2">
            Escalation rules ensure that critical alerts don't go unnoticed.
          </p>
          <p className="text-sm text-slate-600 mb-6 font-medium">
            Create a rule to automatically notify supervisors and managers when critical alerts aren't acknowledged within a specified time.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <PlusIcon className="w-5 h-5" />
            Create Your First Rule
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {rules.map((rule, index) => (
            <div
              key={rule.id}
              className="glass-card p-5 animate-fade-in"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold text-white">{rule.name}</h4>
                    {!rule.isActive && (
                      <span className="badge bg-slate-300 text-slate-800 border border-slate-400 font-bold">
                        Disabled
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-700 mb-3 font-semibold">
                    Escalate after {rule.escalateAfterMinutes} minutes if not acknowledged
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {rule.severities?.map((sev) => (
                      <span key={sev} className="text-xs px-2 py-1 bg-white/10 text-gray-300 rounded-lg">
                        {sev}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditingRule(rule)}
                    className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/10 transition-all"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Delete this escalation rule?')) {
                        deleteMutation.mutate(rule.id);
                      }
                    }}
                    className="p-2 rounded-xl text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showAddModal || editingRule) && (
        <EscalationRuleModal
          rule={editingRule}
          siteId={siteId}
          onClose={() => {
            setShowAddModal(false);
            setEditingRule(null);
          }}
        />
      )}
    </div>
  );
}

function EscalationRuleModal({ rule, onClose, siteId }) {
  const queryClient = useQueryClient();
  const isEdit = !!rule;

  const [formData, setFormData] = useState({
    siteId: rule?.siteId || siteId,
    name: rule?.name || '',
    escalateAfterMinutes: rule?.escalateAfterMinutes || 15,
    severities: rule?.severities || ['CRITICAL', 'HIGH'],
    categories: rule?.categories || [],
    escalationChain: rule?.escalationChain || [
      { level: 1, notifyRoles: ['supervisor'], channels: ['email', 'push'] },
    ],
    isActive: rule?.isActive ?? true,
  });

  const createMutation = useMutation({
    mutationFn: notificationsAPI.createEscalationRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['escalation-rules', siteId] });
      toast.success('Rule created');
      onClose();
    },
    onError: () => toast.error('Failed to create rule'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => notificationsAPI.updateEscalationRule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['escalation-rules', siteId] });
      toast.success('Rule updated');
      onClose();
    },
    onError: () => toast.error('Failed to update rule'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEdit) {
      updateMutation.mutate({ id: rule.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative glass-card w-full max-w-lg animate-scale-in">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white">
            {isEdit ? 'Edit Escalation Rule' : 'New Escalation Rule'}
          </h3>
          <button onClick={onClose} className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/10 transition-all">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Rule Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              placeholder="e.g., Critical Alert Escalation"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Escalate After (minutes)
            </label>
            <input
              type="number"
              value={formData.escalateAfterMinutes}
              onChange={(e) =>
                setFormData({ ...formData, escalateAfterMinutes: parseInt(e.target.value) })
              }
              className="input"
              min="1"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Apply to Severities
            </label>
            <div className="flex flex-wrap gap-2">
              {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map((sev) => {
                const isSelected = formData.severities.includes(sev);
                return (
                  <button
                    key={sev}
                    type="button"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        severities: isSelected
                          ? formData.severities.filter((s) => s !== sev)
                          : [...formData.severities, sev],
                      });
                    }}
                    className={clsx(
                      'px-3 py-1.5 rounded-lg border text-sm font-medium transition-all duration-300',
                      isSelected
                        ? 'bg-purple-500/20 border-purple-500/30 text-purple-400'
                        : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                    )}
                  >
                    {sev}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="flex items-center gap-3 cursor-pointer group">
            <div className="relative">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="sr-only"
              />
              <div className={clsx(
                'w-5 h-5 rounded-md border transition-all duration-200',
                formData.isActive
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 border-purple-500'
                  : 'border-white/30 group-hover:border-white/50'
              )}>
                {formData.isActive && (
                  <CheckIcon className="w-5 h-5 text-white" />
                )}
              </div>
            </div>
            <span className="text-sm text-gray-300">Rule is active</span>
          </label>
        </form>

        <div className="flex justify-end gap-3 p-5 border-t border-white/10">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-primary"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {isEdit ? 'Update Rule' : 'Create Rule'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NotificationHistory() {
  const [page, setPage] = useState(1);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['notification-history', page],
    queryFn: () => notificationsAPI.getHistory({ page, limit: 20 }),
  });

  const notifications = data?.data?.data || [];
  const pagination = data?.data?.pagination;

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      queryClient.invalidateQueries({ queryKey: ['notification-history'] });
      toast.success('Notification marked as read');
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleViewAlert = (alertId) => {
    if (alertId) {
      // Navigate to alerts page filtered to this alert
      window.location.href = `/dashboard/alerts?alertId=${alertId}`;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500/30 border-t-purple-500"></div>
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="space-y-4">
        <div className="mb-4">
          <h3 className="text-3xl font-bold text-white mb-2">📋 Notification History</h3>
          <p className="text-base text-gray-300 font-medium">Complete audit trail of all notifications sent to you</p>
        </div>
        <div className="glass-card p-12 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
            <ClockIcon className="w-10 h-10 text-purple-400" />
          </div>
          <h3 className="text-2xl font-black text-slate-800 mb-2">No Notifications Yet</h3>
          <p className="text-slate-700 font-semibold">Notifications will appear here as they are delivered via email, SMS, or push</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <h3 className="text-3xl font-black text-white mb-2">📋 Notification History</h3>
        <p className="text-base text-slate-900 font-bold">Complete audit trail of {notifications.length}+ notifications - showing latest first</p>
      </div>
      <div className="glass-card overflow-hidden">
        <table className="glass-table">
          <thead>
            <tr>
              <th className="text-left">📌 Type</th>
              <th className="text-left">📤 Channel</th>
              <th className="text-left">📧 Subject</th>
              <th className="text-left">✓ Status</th>
              <th className="text-left">🕐 Sent At</th>
            </tr>
          </thead>
          <tbody>
            {notifications.map((notif, index) => (
              <tr
                key={notif.id}
                className="animate-fade-in cursor-pointer hover:bg-slate-100 transition-colors"
                style={{ animationDelay: `${index * 0.03}s` }}
                onClick={() => {
                  setSelectedNotification(notif);
                  setShowDetailModal(true);
                }}
              >
                <td className="text-slate-900 font-semibold">{notif.type?.replace(/_/g, ' ') || 'Notification'}</td>
                <td>
                  <span className="badge bg-slate-100 text-slate-900 border border-slate-300 font-bold">
                    {notif.channel}
                  </span>
                </td>
                <td className="text-slate-900 font-bold">{notif.subject || '-'}</td>
                <td>
                  <span className={clsx(
                    'badge',
                    notif.status === 'sent' ? 'badge-success' : 'badge-critical'
                  )}>
                    {notif.status}
                  </span>
                </td>
                <td className="text-slate-900 font-bold">
                  {formatDateTime(notif.createdAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination && pagination.pages > 1 && (
        <div className="flex justify-center items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn btn-secondary"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-slate-700 font-bold">
            Page {page} of {pagination.pages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
            disabled={page === pagination.pages}
            className="btn btn-secondary"
          >
            Next
          </button>
        </div>
      )}

      {/* Notification Detail Modal */}
      <NotificationDetailModal
        isOpen={showDetailModal}
        notification={selectedNotification}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedNotification(null);
        }}
        onMarkAsRead={handleMarkAsRead}
        onViewAlert={handleViewAlert}
      />
    </div>
  );
}
